
import { computed, defineComponent, onBeforeMount, PropType, ref } from 'vue'

type NewCalendarFormat = 'iso' | 'readable' | 'date'
type NewCalendarValue = Date | string | Array<string> | Array<Date>
type NewCalendarDisabledDates = 'before-today' | 'after-today' | Array<string> | 'week-limit'

// ISO - 2024-06-03
// Readable - 03.06.2024
// Date - Date object

// disabledDates prop array of strings supports only ISO format | Example: ['2024-06-03', '2024-06-04']
// week-limit only works with range = true

import NewButton from '@/ui-kit/NewButton/index.vue'

export default defineComponent({
    name: 'NewCalendar',
    components: {
        NewButton,
    },
    props: {
        disabledDates: {
            type: [Array, String] as PropType<NewCalendarDisabledDates>,
            default: null,
        },
        format: {
            type: String as PropType<NewCalendarFormat>,
            default: 'iso',
        },
        value: {
            type: [Date, String, Array] as PropType<NewCalendarValue>,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        range: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:value', 'change'],
    setup(props, { emit }) {
        const currentDate = ref(null as Date | null)
        const dates = ref([] as Date[])

        const formatDate = (date: Date, format: Exclude<NewCalendarFormat, 'date'>) => {
            const year = date.getFullYear()
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const day = date.getDate().toString().padStart(2, '0')

            if (format === 'iso') {
                return `${year}-${month}-${day}`
            }

            return `${day}.${month}.${year}`
        }

        const readableToDate = (readableDate: string) => {
            const [day, month, year] = readableDate.split('.').map(Number)
            return new Date(year, month - 1, day)
        }

        const getCurrentMonthYear = () => {
            const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long' }
            const date = currentDate.value || null

            if (date) {
                return date.toLocaleDateString('ru-RU', options)
            }

            return '-'
        }

        const fillDates = () => {
            const filledDates: Date[] = []

            if (!currentDate.value) {
                return []
            }

            const year = currentDate.value.getFullYear()
            const month = currentDate.value.getMonth()

            const firstDayOfMonth = new Date(year, month, 1)
            const lastDayOfMonth = new Date(year, month + 1, 0)
            const firstDayOfWeek = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1

            // Добавляем дни из предыдущего месяца
            for (let i = firstDayOfWeek; i > 0; i--) {
                const prevDate = new Date(firstDayOfMonth)
                prevDate.setDate(firstDayOfMonth.getDate() - i)
                filledDates.push(prevDate)
            }

            // Добавляем все дни текущего месяца
            for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
                filledDates.push(new Date(year, month, day))
            }

            // Добавляем дни из следующего месяца
            const remainingDays = 42 - filledDates.length // 6 недель по 7 дней = 42 дня
            for (let i = 1; i <= remainingDays; i++) {
                filledDates.push(new Date(year, month + 1, i))
            }

            dates.value = filledDates
        }

        const initializeComponent = () => {
            currentDate.value = new Date()
            fillDates()
        }

        onBeforeMount(initializeComponent)

        const onNextMonth = () => {
            if (!currentDate.value || props.disabled) return
            currentDate.value.setMonth(currentDate.value.getMonth() + 1)
            fillDates()
        }

        const onPreviousMonth = () => {
            if (!currentDate.value || props.disabled) return
            currentDate.value.setMonth(currentDate.value.getMonth() - 1)
            fillDates()
        }

        const onSelectDate = (date: Date) => {
            if (props.disabled || isDateDisabled(date)) return

            if (props.range) {
                let formattedDate = null as string | Date | null

                if (props.format === 'iso' || props.format === 'readable') {
                    formattedDate = formatDate(date, props.format)
                } else {
                    formattedDate = date
                }

                if (Array.isArray(props.value)) {
                    if (props.value.length === 0 || props.value.length === 2) {
                        emit('update:value', [formattedDate])
                        emit('change', [formattedDate])
                    }

                    if (props.value.length === 1) {
                        let currentArray = props.value as Array<string | Date>
                        currentArray.push(formattedDate)

                        let valueMinDate = null as Date | null

                        if (props.format === 'iso') {
                            valueMinDate = new Date(props.value[0] as string)
                        }

                        if (props.format === 'readable') {
                            valueMinDate = readableToDate(props.value[0] as string)
                        }

                        if (props.format === 'date') {
                            valueMinDate = props.value[0] as Date
                        }

                        if (isDateLess(date, valueMinDate as Date)) {
                            currentArray = currentArray.reverse()
                        }

                        emit('update:value', currentArray)
                        emit('change', currentArray)
                    }
                } else {
                    emit('update:value', [formattedDate])
                    emit('change', [formattedDate])
                }

                return
            }

            if (isIsoFormat.value) {
                emit('update:value', formatDate(date, props.format as 'iso'))
                emit('change', formatDate(date, props.format as 'iso'))
                return
            }

            if (isReadableFormat.value) {
                emit('update:value', formatDate(date, props.format as 'readable'))
                emit('change', formatDate(date, props.format as 'readable'))
                return
            }

            if (isDateFormat.value) {
                emit('update:value', date)
                emit('change', date)
                return
            }
        }

        const isDateEquals = (dateOne: Date, dateTwo: Date) => {
            return (
                dateOne.getDate() === dateTwo.getDate() &&
                dateOne.getMonth() === dateTwo.getMonth() &&
                dateOne.getFullYear() === dateTwo.getFullYear()
            )
        }

        const isDateToday = (date: Date) => {
            const now = new Date()
            return isDateEquals(date, now)
        }

        const isDateSelected = (date: Date) => {
            if (!props.value) return false

            if (props.range) {
                if (Array.isArray(props.value)) {
                    return props.value.some((valueDate: string | Date) => {
                        if (props.format === 'iso') return isDateEquals(date, new Date(valueDate))
                        if (props.format === 'readable')
                            return isDateEquals(date, readableToDate(valueDate as string))
                        return isDateEquals(date, valueDate as Date)
                    })
                }

                return false
            }

            if (isIsoFormat.value) {
                const valueDate = new Date(props.value as string)
                return isDateEquals(valueDate, date)
            }

            if (isReadableFormat.value) {
                const valueDate = readableToDate(props.value as string)
                return isDateEquals(valueDate, date)
            }

            return isDateEquals(props.value as Date, date)
        }

        const isWeekend = (date: Date): boolean => {
            const day = date.getDay()
            return day === 0 || day === 6
        }

        const isDateLess = (dateOne: Date, dateTwo: Date): boolean => {
            const date_one = new Date(dateOne)
            date_one.setHours(0, 0, 0, 0)

            const date_two = new Date(dateTwo)
            date_two.setHours(0, 0, 0, 0)

            return date_one < date_two
        }

        const isDateMore = (dateOne: Date, dateTwo: Date): boolean => {
            const date_one = new Date(dateOne)
            date_one.setHours(0, 0, 0, 0)

            const date_two = new Date(dateTwo)
            date_two.setHours(0, 0, 0, 0)

            return date_one > date_two
        }

        const isDateDisabled = (date: Date): boolean => {
            if (props.disabledDates) {
                if (typeof props.disabledDates === 'string') {
                    if (props.disabledDates === 'before-today') {
                        return isDateLess(date, new Date())
                    }

                    if (props.disabledDates === 'after-today') {
                        return isDateMore(date, new Date())
                    }

                    if (props.disabledDates === 'week-limit' && props.range) {
                        if (Array.isArray(props.value) && props.value.length) {
                            const firstDate = new Date(props.value[0])
                            const limitDate = new Date(props.value[0])
                            limitDate.setDate(limitDate.getDate() + 6)

                            return isDateLess(date, firstDate) || isDateMore(date, limitDate)
                        }
                    }
                }

                if (Array.isArray(props.disabledDates)) {
                    if (props.disabledDates.length === 0) return false
                    return props.disabledDates.includes(formatDate(date, 'iso'))
                }
            }

            return false
        }

        const isDateInRange = (date: Date) => {
            if (props.range) {
                if (Array.isArray(props.value) && props.value.length === 2) {
                    let valueDateMin = null as Date | null
                    let valueDateMax = null as Date | null

                    if (props.format === 'readable') {
                        valueDateMin = readableToDate(props.value[0] as string)
                        valueDateMax = readableToDate(props.value[1] as string)
                    }

                    if (props.format === 'iso') {
                        valueDateMin = new Date(props.value[0] as string)
                        valueDateMax = new Date(props.value[1] as string)
                    }

                    if (props.format === 'date') {
                        valueDateMin = props.value[0] as Date
                        valueDateMax = props.value[1] as Date
                    }

                    return (
                        isDateMore(date, valueDateMin as Date) &&
                        isDateLess(date, valueDateMax as Date)
                    )
                }

                return false
            }

            return false
        }

        const onReset = () => {
            if (props.disabled) return

            if (props.range) {
                emit('update:value', [])
                emit('change', [])

                return
            }

            emit('update:value', null)
            emit('change', null)
        }

        const isIsoFormat = computed(() => {
            return props.format === 'iso'
        })

        const isReadableFormat = computed(() => {
            return props.format === 'readable'
        })

        const isDateFormat = computed(() => {
            return props.format === 'date'
        })

        return {
            getCurrentMonthYear,
            dates,
            onNextMonth,
            onPreviousMonth,
            onSelectDate,
            onReset,
            isDateSelected,
            isWeekend,
            isDateToday,
            isDateDisabled,
            isDateInRange,
        }
    },
})
